import { z } from 'zod';
import { paginatedSchema } from './api-error.js';
import { sortQuerySchema } from './sort.js';

/**
 * Outcome of a single check run. `skip` means the check could not run (missing
 * prerequisite), which is different from passing.
 *
 * @since 1.0.0
 */
export const CHECK_STATUSES = ['pass', 'warn', 'fail', 'skip', 'running'] as const;

export const checkStatusSchema = z.enum(CHECK_STATUSES);

export type CheckStatus = z.infer<typeof checkStatusSchema>;

/**
 * Categories in the check registry, in display order.
 *
 * @since 1.0.0
 */
export const CHECK_CATEGORIES = ['mail', 'forms', 'content', 'tech', 'ux'] as const;

export const checkCategorySchema = z.enum(CHECK_CATEGORIES);

export type CheckCategory = z.infer<typeof checkCategorySchema>;

/**
 * Lifecycle of a check definition. `live` runs and alerts, `baseline` runs and
 * records but never alerts (it is still learning), `planned` is defined but has no
 * implementation yet.
 *
 * @since 1.0.0
 */
export const CHECK_STATES = ['live', 'baseline', 'planned'] as const;

export const checkStateSchema = z.enum(CHECK_STATES);

export type CheckState = z.infer<typeof checkStateSchema>;

/**
 * How often a check runs. `realtime` checks are evaluated on webhook ingest rather
 * than on a cron.
 *
 * @since 1.0.0
 */
export const CHECK_SCHEDULES = ['realtime', 'hourly', '6-hourly', 'nightly', 'daily', 'weekly', 'on-deploy'] as const;

export const checkScheduleSchema = z.enum(CHECK_SCHEDULES);

export type CheckSchedule = z.infer<typeof checkScheduleSchema>;

/**
 * What caused a check run: the cron sweep, a person, the Checkly schedule or an inbound webhook.
 *
 * @since 1.3.0
 */
export const CHECK_TRIGGERS = ['cron', 'manual', 'checkly-schedule', 'webhook'] as const;

/**
 * The run trigger as a wire enum.
 *
 * @since 1.3.0
 */
export const checkTriggerSchema = z.enum(CHECK_TRIGGERS);

export type CheckTrigger = z.infer<typeof checkTriggerSchema>;

/**
 * Metadata every check file declares about itself. Code is the source of truth for
 * this; the database only stores whether a check is switched on.
 *
 * @since 1.0.0
 */
export const checkMetaSchema = z.object({
    slug: z.string(),
    name: z.string(),
    /** Column heading for the client status table, where space is tight. */
    shortName: z.string().optional(),
    category: checkCategorySchema,
    description: z.string(),
    schedule: checkScheduleSchema,
    state: checkStateSchema,
});

export type CheckMeta = z.infer<typeof checkMetaSchema>;

/**
 * Evidence is a flat map of snake_case keys rendered verbatim in the drawer, so
 * whoever reads an alert sees exactly what the check saw.
 *
 * @since 1.0.0
 */
export const evidenceSchema = z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()]));

export type Evidence = z.infer<typeof evidenceSchema>;

/**
 * A registry row: the check's own metadata joined with its enablement state.
 *
 * @since 1.0.0
 */
export const checkRegistryRowSchema = checkMetaSchema.extend({
    isEnabled: z.boolean(),
    clientsEnabled: z.number().int(),
});

export type CheckRegistryRow = z.infer<typeof checkRegistryRowSchema>;

/**
 * One page of the check registry.
 *
 * @since 1.3.0
 */
export const checkRegistryPageSchema = paginatedSchema(checkRegistryRowSchema);

export type CheckRegistryPage = z.infer<typeof checkRegistryPageSchema>;

/**
 * Columns the check registry may be sorted on, named after the table's own column keys.
 *
 * @since 1.4.0
 */
export const CHECK_SORT_KEYS = ['check', 'description', 'schedule', 'state', 'enabled'] as const;

export const checkSortKeySchema = z.enum(CHECK_SORT_KEYS);

export type CheckSortKey = z.infer<typeof checkSortKeySchema>;

export const checkRegistryQuerySchema = sortQuerySchema(CHECK_SORT_KEYS, 'check', 'asc');

export type CheckRegistryQuery = z.infer<typeof checkRegistryQuerySchema>;

export const checkRunSchema = z.object({
    qcRunId: z.string(),
    clientId: z.number().int(),
    clientSlug: z.string(),
    checkSlug: z.string(),
    formId: z.number().int().nullable(),
    status: checkStatusSchema,
    summary: z.string(),
    evidence: evidenceSchema,
    startedAt: z.number().int(),
    finishedAt: z.number().int().nullable(),
    trigger: checkTriggerSchema,
});

export type CheckRun = z.infer<typeof checkRunSchema>;

/**
 * How the run detail route wraps one run.
 *
 * @since 1.3.0
 */
export const checkRunEnvelopeSchema = z.object({
    run: checkRunSchema,
});

export type CheckRunEnvelope = z.infer<typeof checkRunEnvelopeSchema>;

/**
 * One page of a client's run history for one check.
 *
 * @since 1.3.0
 */
export const checkRunsPageSchema = paginatedSchema(checkRunSchema);

export type CheckRunsPage = z.infer<typeof checkRunsPageSchema>;

export const toggleCheckRequestSchema = z.object({
    isEnabled: z.boolean(),
});

export type ToggleCheckRequest = z.infer<typeof toggleCheckRequestSchema>;

/**
 * What switching a check on or off returns: the check and the state it now has.
 *
 * @since 1.3.0
 */
export const checkToggleResponseSchema = z.object({
    slug: z.string(),
    isEnabled: z.boolean(),
});

export type CheckToggleResponse = z.infer<typeof checkToggleResponseSchema>;

/**
 * What the run-all route returns once the sweep has been handed to the background.
 *
 * @since 1.3.0
 */
export const queuedResponseSchema = z.object({
    queued: z.literal(true),
});

export type QueuedResponse = z.infer<typeof queuedResponseSchema>;
